import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/metaPixel";

const AFTER_FIELDS = [
  { key: "telefon", label: "Telefon", type: "tel" },
  { key: "ansprechpartner", label: "Ansprechpartner", type: "text" },
  { key: "email", label: "E-Mail", type: "email" },
  { key: "password", label: "Passwort", type: "password" },
  { key: "passwordConfirm", label: "Passwort bestätigen", type: "password" },
];

const LAENDER = ["Schweiz", "Deutschland", "Österreich"];

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firmenname: "",
    strasse: "",
    hausnummer: "",
    plz: "",
    ort: "",
    land: "",
    telefon: "",
    ansprechpartner: "",
    email: "",
    homepage: "",
    unternehmens_id: "",
    password: "",
    passwordConfirm: "",
  });
  const [gewerblich, setGewerblich] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const REQUIRED = [
    "firmenname", "strasse", "hausnummer", "plz", "ort", "land",
    "telefon", "ansprechpartner", "email", "password", "passwordConfirm",
  ];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (REQUIRED.some((k) => !form[k].trim())) {
      setError("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }
    if (!gewerblich) {
      setError("Bitte bestätigen Sie, dass Sie sich als gewerblicher Kunde registrieren.");
      return;
    }
    if (form.password !== form.passwordConfirm) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }
    if (form.password.length < 6) {
      setError("Das Passwort muss mindestens 6 Zeichen lang sein.");
      return;
    }
    setLoading(true);
    try {
      const { passwordConfirm, ...rest } = form;
      await api.post("/auth/register", { ...rest, gewerblich: true });
      trackEvent("CompleteRegistration");
      setDone(true);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthLayout title="Vielen Dank" testid="register-success-page">
        <div className="text-center space-y-4 text-charcoal/70 leading-relaxed" data-testid="register-success-message">
          <p>Vielen Dank für Ihre Registrierung.</p>
          <p>Ihr Konto wird nun von unserem Team geprüft.</p>
          <p>Nach erfolgreicher Verifizierung erhalten Sie automatisch eine E-Mail.</p>
          <p>Sobald Ihr Konto freigeschaltet wurde, können Sie individuelle personalisierte Trauersongs erstellen.</p>
        </div>
        <Button
          onClick={() => navigate("/login")}
          className="w-full mt-8 bg-gold hover:bg-golddark text-white rounded-sm py-6 transition-colors duration-500"
          data-testid="register-to-login-button"
        >
          Zur Anmeldung
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Registrieren"
      subtitle="Registrieren Sie Ihr Bestattungsunternehmen, um individuelle personalisierte Trauersongs anzubieten."
      testid="register-page"
    >
      <form onSubmit={submit} className="space-y-5">
        {error && (
          <p className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-sm px-4 py-3" data-testid="register-error">
            {error}
          </p>
        )}

        <div className="space-y-2">
          <Label htmlFor="firmenname">Firmenname</Label>
          <Input
            id="firmenname"
            type="text"
            value={form.firmenname}
            onChange={(e) => setField("firmenname", e.target.value)}
            data-testid="register-firmenname-input"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2 space-y-2">
            <Label htmlFor="strasse">Straße</Label>
            <Input
              id="strasse"
              type="text"
              value={form.strasse}
              onChange={(e) => setField("strasse", e.target.value)}
              data-testid="register-strasse-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hausnummer">Hausnummer</Label>
            <Input
              id="hausnummer"
              type="text"
              value={form.hausnummer}
              onChange={(e) => setField("hausnummer", e.target.value)}
              data-testid="register-hausnummer-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="plz">PLZ</Label>
            <Input
              id="plz"
              type="text"
              value={form.plz}
              onChange={(e) => setField("plz", e.target.value)}
              data-testid="register-plz-input"
            />
          </div>
          <div className="sm:col-span-2 space-y-2">
            <Label htmlFor="ort">Ort</Label>
            <Input
              id="ort"
              type="text"
              value={form.ort}
              onChange={(e) => setField("ort", e.target.value)}
              data-testid="register-ort-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="land">Land</Label>
          <Select value={form.land} onValueChange={(v) => setField("land", v)}>
            <SelectTrigger id="land" data-testid="register-land-select">
              <SelectValue placeholder="Land wählen" />
            </SelectTrigger>
            <SelectContent>
              {LAENDER.map((l) => (
                <SelectItem key={l} value={l}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {AFTER_FIELDS.map((f) => (
          <div key={f.key} className="space-y-2">
            <Label htmlFor={f.key}>{f.label}</Label>
            <Input
              id={f.key}
              type={f.type}
              value={form[f.key]}
              onChange={(e) => setField(f.key, e.target.value)}
              data-testid={`register-${f.key}-input`}
            />
          </div>
        ))}

        <div className="space-y-2">
          <Label htmlFor="homepage">Homepage <span className="text-charcoal/40">(optional)</span></Label>
          <Input
            id="homepage"
            type="text"
            placeholder="https://…"
            value={form.homepage}
            onChange={(e) => setField("homepage", e.target.value)}
            data-testid="register-homepage-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="unternehmens_id">Unternehmens-ID / UID / USt-ID <span className="text-charcoal/40">(optional)</span></Label>
          <Input
            id="unternehmens_id"
            type="text"
            value={form.unternehmens_id}
            onChange={(e) => setField("unternehmens_id", e.target.value)}
            data-testid="register-unternehmensid-input"
          />
        </div>

        <div className="flex items-start gap-3 pt-1">
          <Checkbox
            id="gewerblich"
            checked={gewerblich}
            onCheckedChange={(v) => setGewerblich(!!v)}
            className="mt-1"
            data-testid="register-gewerblich-checkbox"
          />
          <label htmlFor="gewerblich" className="text-sm text-charcoal/80 leading-relaxed">
            Ich bestätige, dass ich mich als gewerblicher Kunde registriere.
          </label>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-gold hover:bg-golddark text-white rounded-sm py-6 transition-colors duration-500"
          data-testid="register-submit-button"
        >
          {loading ? "Wird gesendet…" : "Registrieren"}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-charcoal/50">
        Bereits registriert?{" "}
        <Link to="/login" className="text-gold hover:text-golddark transition-colors duration-500" data-testid="to-login-link">
          Anmelden
        </Link>
      </p>
    </AuthLayout>
  );
}
